import { useCallback, useEffect } from 'react';

import { trackEvent } from '@modernloop/shared/analytics';
import { useShouldLogStatistics } from '@modernloop/shared/hooks';

import { LoggerEvent } from 'src/constants/logger';

import useOrgId from './useOrgId';

export * from 'src/constants/logger';

type LoggerValue = string | number | boolean | string[] | number[] | boolean[];
type LoggerValueWithEmpties = LoggerValue | undefined | null;

type LoggerData = {
  [key: string]: LoggerValue;
};

type LoggerDataWithEmpties = {
  [key: string]: LoggerValueWithEmpties;
};

const logEventHelper = (shouldLogStatistics: boolean, orgId: string | null, event: string, data?: LoggerData) => {
  if (!shouldLogStatistics || import.meta.env.MODE === 'test') {
    return;
  }
  const properties = data ?? {};
  console.log(`Event ${event} was logged.`, properties);
  // Fan out through the analytics facade; org grouping and per-vendor payload
  // handling (LogRocket empties filter, Amplitude sessionUrl enrichment) live in the sinks.
  trackEvent(event, properties, orgId ? { groups: { organizationID: orgId } } : undefined);
};

/**
 * This hook logs an event every time once on render
 * @param event - event to log
 * @param data - data to log
 * @returns void
 * @example
 *  const data = { applicationId: '123' };
 *  useLogEventOnce(LoggerEvent.CLIENT_SCHEDULE_FLOW_BEGIN, {
 *    applicationId: '123'
 *  });
 */
export const useLogEventOnce = (event: LoggerEvent, data: LoggerData = {}) => {
  const orgId = useOrgId();
  const shouldLogStatistics = useShouldLogStatistics();
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    logEventHelper(shouldLogStatistics, orgId, event, data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, shouldLogStatistics, event, JSON.stringify(data)]);
};

/**
 * This hook logs an event if the condition is true.
 * @param shouldLog - boolean condition to log the event
 * @param event - event to log
 * @param data - data to log
 * @returns void
 * @example
 *   const data = { applicationId: '123' };
 *   useLogEventIf(!loading, LoggerEvent.CLIENT_SCHEDULE_FLOW_BEGIN, {
 *     applicationId: '123'
 *   });
 */
export const useLogEventIf = (shouldLog: boolean, event: LoggerEvent, data: LoggerData = {}) => {
  const orgId = useOrgId();
  const shouldLogStatistics = useShouldLogStatistics();
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    if (shouldLog) {
      logEventHelper(shouldLogStatistics, orgId, event, data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldLog, orgId, shouldLogStatistics, event, JSON.stringify(data)]);
};

/**
 * This hook returns a function that can be used to log an event.
 * @returns logEvent function
 * @example
 *  const { logEvent } = useLogEvent();
 *  logEvent(LoggerEvent.CLIENT_SCHEDULE_FLOW_BEGIN, {
 *    applicationId: '123'
 *  });
 */
export const useLogEvent = (): { logEvent: (event: LoggerEvent, data?: LoggerDataWithEmpties) => void } => {
  const orgId = useOrgId();
  const shouldLogStatistics = useShouldLogStatistics();
  const logEvent = useCallback(
    (event: string, data?: LoggerData) => logEventHelper(shouldLogStatistics, orgId, event, data),
    [orgId, shouldLogStatistics]
  );

  return { logEvent };
};
